import { MapPin } from 'lucide-react';
import type { AiCampaignForm } from '@/features/campaigns/types/campaign';
import { TargetedCities } from '@/features/campaigns/components/TargetedCities';
import { FieldError, FieldLabel, campInputClass } from '@/features/campaigns/components/FormPrimitives';
import {
  campaignDateMax,
  validateCampaignDateRange,
} from '@/features/campaigns/utils/campaignUtils';

type WhenWhereSectionProps = {
  formData: AiCampaignForm;
  setFormData: React.Dispatch<React.SetStateAction<AiCampaignForm>>;
  errors: Record<string, string>;
  clearFieldError: (field: string) => void;
  minStart: string;
  maxStart: string;
};

export function WhenWhereSection({
  formData,
  setFormData,
  errors,
  clearFieldError,
  minStart,
  maxStart,
}: WhenWhereSectionProps) {
  const maxEnd = formData.startDate
    ? campaignDateMax(formData.startDate)
    : maxStart;
  const rangeErrors = validateCampaignDateRange(
    formData.startDate,
    formData.endDate,
    minStart,
  );
  const startError =
    errors.startDate || (formData.startDate ? rangeErrors.startDate : '') || '';
  const endError =
    errors.endDate || (formData.endDate ? rangeErrors.endDate : '') || '';

  return (
    <section className="camp-section text-left">
      <div className="mb-4 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-[var(--dash-brand)]" />
        <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
          When & where
        </h2>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <FieldLabel required hint={`Earliest ${minStart}`}>
            Start date
          </FieldLabel>
          <input
            type="date"
            min={minStart}
            max={maxStart}
            value={formData.startDate}
            aria-invalid={Boolean(startError)}
            onChange={(e) => {
              clearFieldError('startDate');
              setFormData((prev) => ({
                ...prev,
                startDate: e.target.value,
                endDate:
                  prev.endDate && prev.endDate < e.target.value ? '' : prev.endDate,
              }));
            }}
            className={`camp-date-input ${campInputClass} ${startError ? 'is-invalid' : ''}`}
          />
          <FieldError message={startError} />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <FieldLabel
            required
            hint={
              formData.startDate
                ? `On or after ${formData.startDate}`
                : 'Pick a start date first'
            }
          >
            End date
          </FieldLabel>
          <input
            type="date"
            min={formData.startDate || minStart}
            max={maxEnd}
            value={formData.endDate}
            disabled={!formData.startDate}
            aria-invalid={Boolean(endError)}
            onChange={(e) => {
              clearFieldError('endDate');
              setFormData((prev) => ({ ...prev, endDate: e.target.value }));
            }}
            className={`camp-date-input ${campInputClass} disabled:opacity-50 ${
              endError ? 'is-invalid' : ''
            }`}
          />
          <FieldError message={endError} />
        </div>
      </div>

      <div className="mt-4">
        <TargetedCities
          selectedCities={formData.targetCities}
          error={errors.targetCities}
          onChange={(cities) => {
            clearFieldError('targetCities');
            setFormData((prev) => ({ ...prev, targetCities: cities }));
          }}
        />
      </div>
    </section>
  );
}
